import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface Slide {
  id: string;
  image: string;
  tag: string;
  title: string;
  desc: string;
  to: string;
  cta: string;
}

const slides: Slide[] = [
  {
    id: 's1',
    image:
      'https://readdy.ai/api/search-image?query=cinematic%20wide%20editorial%20scene%20of%20an%20elegant%20woman%20silhouetted%20against%20neon%20city%20rain%20at%20night%2C%20muted%20grey%20and%20deep%20red%20tones%2C%20moody%20film%20grain%2C%20dramatic%20lighting%2C%20high%20detail&width=1600&height=900&seq=ehero-01&orientation=landscape',
    tag: '作品发布',
    title: '《夜色微光》正式定档 11 月',
    desc: '深田咏美领衔主演的都市悬疑电影释出首支预告，聚焦都市孤独者的情感困境。',
    to: '/news/n001',
    cta: '查看详情',
  },
  {
    id: 's2',
    image:
      'https://readdy.ai/api/search-image?query=cinematic%20wide%20shot%20of%20a%20dim%20film%20studio%20with%20a%20single%20warm%20spotlight%20and%20a%20deep%20red%20curtain%2C%20muted%20grey%20tones%2C%20atmospheric%20dusty%20air%2C%20editorial%20photography%2C%20high%20detail&width=1600&height=900&seq=ehero-02&orientation=landscape',
    tag: '精选作品',
    title: '走进深田咏美的光影世界',
    desc: '从电影到纪录片，横跨多年的作品合集，带你认识那些「安静却有力」的角色。',
    to: '/works',
    cta: '浏览作品',
  },
  {
    id: 's3',
    image:
      'https://readdy.ai/api/search-image?query=cinematic%20wide%20editorial%20scene%20of%20an%20elegant%20film%20festival%20stage%20with%20warm%20spotlights%20and%20a%20deep%20red%20carpet%2C%20muted%20grey%20backdrop%2C%20elegant%20atmosphere%2C%20high%20detail&width=1600&height=900&seq=ehero-03&orientation=landscape',
    tag: '活动报道',
    title: '东京国际电影节红毯亮相',
    desc: '深田咏美以特邀嘉宾身份出席开幕式，一袭深红礼服成为全场焦点。',
    to: '/news/n003',
    cta: '阅读报道',
  },
];

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const go = (i: number) => setIndex((i + slides.length) % slides.length);

  return (
    <section className="relative h-[480px] w-full overflow-hidden bg-foreground-950 md:h-[640px]">
      <h1 className="sr-only">深田咏美明星资讯网 - 最新作品与新闻动态</h1>
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === index ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
        >
          <img
            src={slide.image}
            alt={`${slide.title} 焦点图`}
            title={`${slide.title} 深田咏美`}
            className={`h-full w-full object-top ${i === index ? 'animate-slow-zoom' : ''}`}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/20"></div>
        </div>
      ))}

      <div className="relative z-10 flex h-full items-center">
        <div className="w-full max-w-6xl mx-auto px-4 md:px-8">
          <div className="max-w-2xl">
            {slides.map((slide, i) => (
              <div key={slide.id} className={i === index ? 'block animate-fade-up' : 'hidden'}>
                <span className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-background-50">
                  {slide.tag}
                </span>
                <h2 className="mt-5 font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
                  {slide.title}
                </h2>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
                  {slide.desc}
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <Link
                    to={slide.to}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                  >
                    {slide.cta}
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                  <Link
                    to="/profile"
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10 cursor-pointer"
                  >
                    明星资料
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 items-center gap-3 md:bottom-8">
        <button
          type="button"
          aria-label="上一张"
          onClick={() => go(index - 1)}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50/15 cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-lg"></i>
        </button>
        <div className="flex items-center gap-2">
          {slides.map((slide, i) => (
            <button
              key={slide.id}
              type="button"
              aria-label={`第 ${i + 1} 张`}
              onClick={() => go(i)}
              className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                i === index ? 'w-8 bg-primary-400' : 'w-3 bg-background-50/50 hover:bg-background-50/80'
              }`}
            ></button>
          ))}
        </div>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => go(index + 1)}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50/15 cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-lg"></i>
        </button>
      </div>
    </section>
  );
}